import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, LogOut, ArrowLeft, Sparkles, RefreshCw, Check, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Operations", "Leadership", "Assessment", "AI", "Continuous Improvement", "Growth"];
const EMPTY = {
  title: "", excerpt: "", content: "", category: "Operations", tags: "",
  cover_image: "", author: "Local Proof STL", status: "draft", seo_title: "", seo_description: "",
};

// Fields the operator can generate/regenerate individually.
const AI_FIELDS = [
  { key: "topic", label: "Topic idea", target: null },
  { key: "title", label: "Working title", target: "title" },
  { key: "outline", label: "Outline", target: null },
  { key: "body", label: "Full article body", target: "content" },
  { key: "executive_summary", label: "Executive summary", target: null },
  { key: "excerpt", label: "Excerpt", target: "excerpt" },
  { key: "seo_title", label: "SEO title", target: "seo_title" },
  { key: "seo_description", label: "Meta description", target: "seo_description" },
  { key: "slug", label: "URL slug", target: null },
  { key: "linkedin_post", label: "LinkedIn post", target: null },
  { key: "newsletter_summary", label: "Newsletter summary", target: null },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  // AI Assist state (per-field)
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiTopic, setAiTopic] = useState("");
  const [aiOutline, setAiOutline] = useState("");
  const [aiExecSummary, setAiExecSummary] = useState("");
  const [aiSlug, setAiSlug] = useState("");
  const [aiLinkedIn, setAiLinkedIn] = useState("");
  const [aiNewsletter, setAiNewsletter] = useState("");
  const [aiDraft, setAiDraft] = useState({}); // { field: text } for fields that flow into the form
  const [aiLoading, setAiLoading] = useState({}); // { field: bool }

  const loadPosts = useCallback(async () => { setPosts((await api.get("/admin/posts")).data); }, []);
  const loadContacts = useCallback(async () => { setContacts((await api.get("/contact")).data); }, []);
  const loadAnalytics = useCallback(async () => {
    try { setAnalytics((await api.get("/admin/analytics/summary")).data); } catch { /* non-critical */ }
  }, []);

  useEffect(() => { loadPosts(); loadContacts(); loadAnalytics(); }, [loadPosts, loadContacts, loadAnalytics]);

  const openNew = () => { setForm(EMPTY); resetAI(); setEditing("new"); };
  const openEdit = async (id) => {
    const r = await api.get(`/admin/posts/${id}`);
    const p = r.data;
    setForm({ ...p, tags: (p.tags || []).join(", ") });
    resetAI();
    setEditing(p);
  };
  const resetAI = () => {
    setAiInstruction(""); setAiTopic(""); setAiOutline(""); setAiExecSummary("");
    setAiSlug(""); setAiLinkedIn(""); setAiNewsletter(""); setAiDraft({}); setAiLoading({});
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (editing === "new") await api.post("/admin/posts", payload);
      else await api.put(`/admin/posts/${editing.id}`, payload);
      toast.success("Article saved");
      setEditing(null); loadPosts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/posts/${id}`);
    toast.success("Article deleted"); loadPosts();
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const generateAI = async (field) => {
    setAiLoading((s) => ({ ...s, [field]: true }));
    try {
      const context = {
        topic: aiTopic || undefined,
        title: form.title || undefined,
        category: form.category,
        outline: aiOutline || undefined,
        body: form.content || undefined,
        excerpt: form.excerpt || undefined,
      };
      const { data } = await api.post("/admin/ai/cms/generate", { field, context, instruction: aiInstruction });
      const out = data.output;
      // Route by field
      if (field === "topic") setAiTopic(out);
      else if (field === "outline") setAiOutline(out);
      else if (field === "executive_summary") setAiExecSummary(out);
      else if (field === "slug") setAiSlug(out);
      else if (field === "linkedin_post") setAiLinkedIn(out);
      else if (field === "newsletter_summary") setAiNewsletter(out);
      else setAiDraft((d) => ({ ...d, [field]: out }));
      toast.success(`Draft ready: ${field.replace(/_/g, " ")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "AI unavailable");
    } finally {
      setAiLoading((s) => ({ ...s, [field]: false }));
    }
  };

  const acceptDraft = (field) => {
    const spec = AI_FIELDS.find((f) => f.key === field);
    if (!spec || !spec.target) return;
    setForm((f) => ({ ...f, [spec.target]: aiDraft[field] }));
    setAiDraft((d) => { const n = { ...d }; delete n[field]; return n; });
    toast.success("Applied to article");
  };
  const rejectDraft = (field) => {
    setAiDraft((d) => { const n = { ...d }; delete n[field]; return n; });
  };

  // ---------------------------------------------------------------------------
  // Editor view
  // ---------------------------------------------------------------------------
  if (editing) {
    return (
      <div className="min-h-screen bg-surface py-10" data-testid="admin-editor">
        <div className="max-w-5xl mx-auto px-6">
          <button onClick={() => setEditing(null)} className="inline-flex items-center gap-2 text-steel hover:text-ink text-sm mb-6">
            <ArrowLeft size={15} /> Back to articles
          </button>

          <div className="grid lg:grid-cols-12 gap-6">
            {/* MAIN FORM */}
            <form onSubmit={save} className="lg:col-span-8 bg-white border border-border rounded-2xl p-8 space-y-5">
              <h2 className="font-serif text-3xl text-ink">{editing === "new" ? "New article" : "Edit article"}</h2>

              <FormField label="Title" testId="editor-title" value={form.title} onChange={set("title")} />
              <FormField label="Excerpt" testId="editor-excerpt" value={form.excerpt} onChange={set("excerpt")} textarea rows={2} />
              <FormField label='Content (use "## " for section headings)' testId="editor-content" value={form.content} onChange={set("content")} textarea rows={12} mono />

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-charcoal">Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger data-testid="editor-category" className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-charcoal">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                    <SelectTrigger data-testid="editor-status" className="mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="published">Published</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <FormField label="Cover image URL" testId="editor-cover" value={form.cover_image} onChange={set("cover_image")} />
              <div className="grid sm:grid-cols-2 gap-5">
                <FormField label="Tags (comma separated)" testId="editor-tags" value={form.tags} onChange={set("tags")} />
                <FormField label="Author" value={form.author} onChange={set("author")} />
              </div>
              <FormField label="SEO title" value={form.seo_title} onChange={set("seo_title")} />
              <FormField label="SEO description" value={form.seo_description} onChange={set("seo_description")} textarea rows={2} />
              <button type="submit" data-testid="editor-save" disabled={saving} className="bg-ink text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-charcoal transition-colors disabled:opacity-60">
                {saving ? "Saving…" : "Save article"}
              </button>
            </form>

            {/* AI ASSIST PANEL */}
            <aside className="lg:col-span-4 bg-white border border-border rounded-2xl p-6" data-testid="ai-assist-panel">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles size={16} className="text-steel" />
                <h3 className="font-serif text-xl text-ink">Draft with Claude</h3>
              </div>
              <p className="text-xs text-steel mb-5 leading-relaxed">
                Claude drafts. You approve. Nothing publishes automatically.
              </p>

              <Label className="text-charcoal text-xs">Optional instruction for this session</Label>
              <Textarea
                data-testid="ai-instruction"
                value={aiInstruction}
                onChange={(e) => setAiInstruction(e.target.value)}
                rows={2}
                placeholder="e.g. focus on scheduling density, keep it under 700 words"
                className="mt-2 mb-4 text-sm"
              />

              {aiTopic && <ScratchOutput label="Topic idea" testId="ai-out-topic" value={aiTopic} />}
              {aiOutline && <ScratchOutput label="Outline" testId="ai-out-outline" value={aiOutline} mono />}
              {aiExecSummary && <ScratchOutput label="Executive summary" testId="ai-out-exec" value={aiExecSummary} />}
              {aiSlug && <ScratchOutput label="URL slug" testId="ai-out-slug" value={aiSlug} />}
              {aiLinkedIn && <ScratchOutput label="LinkedIn post" testId="ai-out-linkedin" value={aiLinkedIn} />}
              {aiNewsletter && <ScratchOutput label="Newsletter summary" testId="ai-out-newsletter" value={aiNewsletter} />}

              <div className="grid grid-cols-1 gap-2">
                {AI_FIELDS.map((f) => (
                  <div key={f.key} data-testid={`ai-row-${f.key}`}>
                    {aiDraft[f.key] ? (
                      <div className="border border-border rounded-lg p-3 bg-surface">
                        <div className="text-[11px] uppercase tracking-widest text-steel mb-1">{f.label} — draft</div>
                        <p className="text-[13px] text-charcoal whitespace-pre-wrap leading-relaxed max-h-40 overflow-auto">{aiDraft[f.key]}</p>
                        <div className="flex gap-2 mt-3">
                          <button type="button" onClick={() => acceptDraft(f.key)} data-testid={`ai-accept-${f.key}`}
                                  className="inline-flex items-center gap-1 text-xs bg-ink text-white px-3 py-1.5 rounded-full hover:bg-charcoal">
                            <Check size={13} /> Apply
                          </button>
                          <button type="button" onClick={() => generateAI(f.key)} data-testid={`ai-regen-${f.key}`}
                                  className="inline-flex items-center gap-1 text-xs border border-border text-steel px-3 py-1.5 rounded-full hover:text-ink hover:border-ink">
                            <RefreshCw size={13} /> Regenerate
                          </button>
                          <button type="button" onClick={() => rejectDraft(f.key)} data-testid={`ai-reject-${f.key}`}
                                  className="inline-flex items-center gap-1 text-xs text-steel px-3 py-1.5 hover:text-destructive">
                            <X size={13} /> Reject
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => generateAI(f.key)}
                        disabled={!!aiLoading[f.key]}
                        data-testid={`ai-btn-${f.key}`}
                        className="w-full text-left text-sm px-3 py-2 rounded-lg border border-border hover:border-ink/40 hover:bg-surface transition-colors disabled:opacity-60 flex items-center justify-between"
                      >
                        <span className="text-charcoal">{f.label}</span>
                        <span className="text-[11px] text-steel">{aiLoading[f.key] ? "Drafting…" : "Draft"}</span>
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-6 text-[11px] text-steel leading-relaxed">
                Claude drafts are stored as audit records with model + timestamp. Human review is required before any content publishes.
              </p>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Dashboard view
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-surface" data-testid="admin-dashboard">
      <header className="bg-white border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <span className="w-7 h-7 bg-ink text-white flex items-center justify-center font-serif">L</span>
            <span className="font-serif text-lg text-ink">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate("/")} className="text-sm text-steel hover:text-ink">View site</button>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-steel hover:text-ink">
              <LogOut size={15} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-serif text-4xl text-ink mb-8">Welcome back{user?.name ? `, ${user.name}` : ""}.</h1>
        <Tabs defaultValue="articles">
          <TabsList>
            <TabsTrigger value="articles" data-testid="tab-articles">Articles ({posts.length})</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="tab-submissions">Submissions ({contacts.length})</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="articles" className="mt-6">
            <div className="flex justify-end mb-4">
              <button onClick={openNew} data-testid="new-article-btn" className="inline-flex items-center gap-2 bg-ink text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-charcoal">
                <Plus size={16} /> New article
              </button>
            </div>
            <div className="bg-white border border-border rounded-2xl divide-y divide-border">
              {posts.map((p) => (
                <div key={p.id} className="flex items-center justify-between p-5" data-testid={`admin-post-${p.slug}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-ink text-white" : "bg-cloud text-charcoal"}`}>{p.status}</span>
                      <span className="text-xs text-steel">{p.category}</span>
                    </div>
                    <h3 className="font-serif text-xl text-ink mt-1 truncate">{p.title}</h3>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => openEdit(p.id)} data-testid={`edit-${p.slug}`} className="p-2 text-steel hover:text-ink"><Edit3 size={17} /></button>
                    <button onClick={() => remove(p.id)} data-testid={`delete-${p.slug}`} className="p-2 text-steel hover:text-destructive"><Trash2 size={17} /></button>
                  </div>
                </div>
              ))}
              {posts.length === 0 && <p className="p-8 text-steel text-center">No articles yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="submissions" className="mt-6">
            <div className="space-y-4">
              {contacts.map((c) => <SubmissionCard key={c.id} c={c} />)}
              {contacts.length === 0 && <p className="p-8 text-steel text-center">No submissions yet.</p>}
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            <div data-testid="analytics-panel">
              {!analytics ? (
                <p className="p-8 text-steel text-center">Loading analytics…</p>
              ) : (
                <>
                  <p className="text-xs text-steel mb-4">
                    Privacy-conscious first-party analytics — no cookies, no IP storage, no personal identifiers. Last {analytics.days} days.
                  </p>
                  <div className="grid sm:grid-cols-3 gap-4 mb-8">
                    <StatCard testId="analytics-pageviews" label="Page views" value={analytics.page_views_total} />
                    <StatCard testId="analytics-cta" label="CTA clicks" value={analytics.cta_clicks_total} />
                    <StatCard testId="analytics-submissions" label="Contact form submissions" value={analytics.form_submits} />
                  </div>
                  <div className="grid lg:grid-cols-2 gap-4">
                    <div className="bg-white border border-border rounded-2xl p-6">
                      <p className="text-[11px] uppercase tracking-widest text-steel mb-4">Top pages</p>
                      {analytics.top_pages.length === 0 ? (
                        <p className="text-sm text-steel">No visits recorded yet.</p>
                      ) : (
                        <ul className="space-y-2.5" data-testid="analytics-top-pages">
                          {analytics.top_pages.map((p) => (
                            <li key={p.path} className="flex items-center justify-between text-sm">
                              <span className="text-charcoal font-mono text-[13px]">{p.path}</span>
                              <span className="text-steel tabular-nums">{p.views}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="bg-white border border-border rounded-2xl p-6">
                      <p className="text-[11px] uppercase tracking-widest text-steel mb-4">CTA engagement</p>
                      {analytics.cta_breakdown.length === 0 ? (
                        <p className="text-sm text-steel">No CTA clicks recorded yet.</p>
                      ) : (
                        <ul className="space-y-2.5" data-testid="analytics-cta-breakdown">
                          {analytics.cta_breakdown.map((c) => (
                            <li key={c.label} className="flex items-center justify-between text-sm">
                              <span className="text-charcoal font-mono text-[13px]">{c.label}</span>
                              <span className="text-steel tabular-nums">{c.clicks}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
function StatCard({ testId, label, value }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid={testId}>
      <p className="font-serif text-4xl text-ink tabular-nums">{value}</p>
      <p className="text-xs text-steel mt-2 uppercase tracking-widest">{label}</p>
    </div>
  );
}

function FormField({ label, testId, value, onChange, textarea, rows, mono }) {
  return (
    <div>
      <Label className="text-charcoal">{label}</Label>
      {textarea ? (
        <Textarea data-testid={testId} value={value} onChange={onChange} rows={rows} className={`mt-2 ${mono ? "font-mono text-sm" : ""}`} />
      ) : (
        <Input data-testid={testId} value={value} onChange={onChange} className="mt-2" />
      )}
    </div>
  );
}

function ScratchOutput({ label, testId, value, mono }) {
  return (
    <div className="mb-4 border border-border rounded-lg p-3 bg-surface" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-steel mb-1">{label}</div>
      <p className={`text-[13px] text-charcoal whitespace-pre-wrap leading-relaxed max-h-40 overflow-auto ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}

function SubmissionCard({ c }) {
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/intel/${c.id}`);
      setBrief(data);
    } catch { /* ignore */ }
  }, [c.id]);

  useEffect(() => { load(); }, [load]);
  // Poll while pending
  useEffect(() => {
    if (brief?.status !== "pending") return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [brief?.status, load]);

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/intel/${c.id}/regenerate`);
      setBrief({ ...(brief || {}), status: "pending" });
      toast.success("Regenerating brief…");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const statusPill = (s) => {
    const map = {
      ready: "bg-ink text-white",
      pending: "bg-cloud text-charcoal",
      failed: "bg-destructive/10 text-destructive",
      not_started: "bg-surface text-steel",
    };
    const label = { ready: "brief ready", pending: "generating…", failed: "failed", not_started: "no brief" }[s] || s;
    return <span className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-full ${map[s] || "bg-surface text-steel"}`}>{label}</span>;
  };

  return (
    <div className="bg-white border border-border rounded-2xl p-5" data-testid={`submission-${c.id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-serif text-xl text-ink">{c.name}</h3>
            {statusPill(brief?.status || "not_started")}
          </div>
          <p className="text-sm text-steel mt-1">{c.email} · {c.company || "—"} · {c.interest}</p>
        </div>
        <span className="text-xs text-steel">{new Date(c.created_at).toLocaleDateString()}</span>
      </div>
      <p className="text-sm text-charcoal/80 mt-3 leading-relaxed" data-testid={`submission-message-${c.id}`}>{c.message}</p>

      <div className="mt-5 pt-5 border-t border-border">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[11px] uppercase tracking-widest text-steel">Opportunity Intelligence Brief · internal</p>
          <button
            onClick={regenerate} disabled={busy || brief?.status === "pending"}
            data-testid={`intel-regen-${c.id}`}
            className="inline-flex items-center gap-1 text-xs text-steel hover:text-ink disabled:opacity-50"
          >
            <RefreshCw size={13} /> {brief?.status === "pending" ? "Generating…" : "Regenerate"}
          </button>
        </div>
        {brief?.status === "ready" ? (
          <pre data-testid={`intel-content-${c.id}`} className="whitespace-pre-wrap font-sans text-[13px] text-charcoal leading-[1.7] bg-surface border border-border rounded-lg p-4 max-h-96 overflow-auto">{brief.content}</pre>
        ) : brief?.status === "pending" ? (
          <p className="text-sm text-steel italic">Claude is preparing the brief…</p>
        ) : brief?.status === "failed" ? (
          <p className="text-sm text-destructive italic" data-testid={`intel-failed-${c.id}`}>AI briefing unavailable — original submission preserved. {brief?.error ? `(${brief.error})` : ""}</p>
        ) : (
          <p className="text-sm text-steel italic">No brief yet.</p>
        )}
      </div>
    </div>
  );
}
